import React, { useCallback, useEffect, useState } from "react";

import moment from "moment";

import { downloadS3File, getAllFarmVerificationTasks } from "services";

import { getAdminVerificationYearlyDataYear, saveFile, useCancelAsyncOnUnmount } from "utils";

import { ADMIN_VERIFICATION_DEADLINE } from "consts";

import { Anchor, Icon, IconButton, LinedCard, Spin, Tabs } from "components";

import { CustomTaskDrawer } from "pages/admin/verifications/components/drawers/CustomTaskDrawer";
import { SubmitTaskDrawer } from "pages/app/farm/components/SubmitTaskDrawer";

const generateDocumentFilename = (farmName, year, documentName, originalFilename, link) => {
  const sanitize = (str) => (str || "").replace(/\s+/g, "_");

  // Extract extension from link URL
  let extension = "";
  if (link) {
    try {
      const { pathname } = new URL(link);
      const match = pathname.match(/\.([a-z0-9]+)(?:\?|$)/i);
      if (match) {
        extension = `.${match[1].toLowerCase()}`;
      }
    } catch (e) {
      // URL parsing failed, no extension
    }
  }

  const parts = [year, sanitize(documentName), sanitize(farmName), sanitize(originalFilename)].filter(Boolean);

  return `${parts.join("_")}${extension}`;
};

const DocumentCard = ({ farmTask, onClick, farmName }) => {
  const { completedAt, deadline, documents, note, uploaded, task, year } = farmTask;
  const { documentName } = task;

  const getTextColor = () => {
    if (completedAt) {
      return "green-text";
    }
    if (moment(deadline).isBefore(moment())) {
      return "red-text";
    }
    return "grey-text";
  };

  const handleDocumentDownload = async (document) => {
    const filename = generateDocumentFilename(farmName, year, documentName, document.filename, document.link);

    try {
      const data = await downloadS3File(document.link);
      saveFile(data, filename);
    } catch (error) {
      // Fallback: open in new tab if download fails
      window.open(document.link, "_blank");
    }
  };

  return (
    <LinedCard
      className="margin-bottom-100 task-overview-card"
      dataTestId={`document-card-${documentName}-${farmTask.id}`}
      primary={
        <div>
          <div className="task-title">
            <h5 className={completedAt ? "" : getTextColor()}>{documentName}</h5>
            <div className="admin-doc-upload">
              <span className={`paragraph-s-bold ${getTextColor()}`}>
                {completedAt ? "completed" : `due date ${moment(deadline).locale("en").format("Do MMM")}`}
              </span>
              {!completedAt && <IconButton iconName="tabler/file-upload" onClick={() => onClick(farmTask)} type="" className="margin-left-50" />}
            </div>
          </div>
        </div>
      }
      secondary={
        completedAt ? (
          <>
            {completedAt && uploaded && documents?.length ? (
              <div className={`file-list ${note ? "margin-bottom-100" : ""}`}>
                <span className="paragraph-m-bold">Uploaded files:</span>
                {documents.map(({ filename, link }, i) => (
                  <Anchor
                    onClick={() => {
                      handleDocumentDownload({ filename, link });
                      return false; // Prevent navigation
                    }}
                    key={i}
                    style={{ cursor: "pointer" }}
                  >
                    <span className="paragraph-m green-text">{filename}</span>
                    <Icon iconName="tabler/download" className="icon inline link margin-left-50" />
                  </Anchor>
                ))}
              </div>
            ) : null}
            {completedAt && !uploaded ? (
              <div className={`${note ? "margin-bottom-100" : ""}`}>
                <Icon iconName="tabler/alert-triangle" size="s" className="icon inline warning" />
                <span className="paragraph-m-bold margin-left-50 orange-text">Farmer does not have requested document</span>
              </div>
            ) : null}
            {note ? (
              <div className="task-note">
                <span className="paragraph-m-bold">Farmer note</span>
                <span className="paragraph-m">{note}</span>
              </div>
            ) : null}
          </>
        ) : null
      }
    />
  );
};

export const TaskOverview = ({ farmId, farmCountry, farmName }) => {
  const [loading, setLoading] = useState(true);
  const [farmTasks, setFarmTasks] = useState([]);
  const [showCustomTaskDrawer, setShowCustomTaskDrawer] = useState(false);
  const signal = useCancelAsyncOnUnmount()();
  const [showSubmitTaskDrawer, setShowSubmitTaskDrawer] = useState(false);
  const [selectedFarmTask, setSelectedFarmTask] = useState(null);

  const currentVerificationYear = getAdminVerificationYearlyDataYear();

  const handleSubmitDocClick = (farmTask) => {
    setSelectedFarmTask(farmTask);
    setShowSubmitTaskDrawer(true);
  };
  const handleSubmitDocDrawerClose = () => {
    setSelectedFarmTask(null);
    setShowSubmitTaskDrawer(false);
  };

  const years = farmTasks.length ? [...new Set(farmTasks.map(({ year }) => year))].sort((a, b) => b - a) : [];

  const loadFarmTasks = useCallback(() => {
    getAllFarmVerificationTasks({
      farmId,
      signal,
    })
      // We only want to take into account tasks from the current verification year
      .then((res) =>
        setFarmTasks(
          res?.filter(
            (farmTask) => farmTask.completedAt || moment(farmTask.createdAt).isAfter(moment(`${currentVerificationYear}-${ADMIN_VERIFICATION_DEADLINE}`))
          )
        )
      )
      .finally(() => setLoading(false));
  }, [signal, farmId, currentVerificationYear]);

  useEffect(() => {
    loadFarmTasks();
  }, [loadFarmTasks]);

  const getDocumentData = () =>
    years?.map((year, i) => ({
      key: i,
      label: year,
      children: (
        <>
          {farmTasks.length
            ? farmTasks
                .filter((farmTask) => farmTask.year === year)
                .map((farmTask, i) => <DocumentCard farmTask={farmTask} key={i} onClick={handleSubmitDocClick} farmName={farmName} />)
            : null}
        </>
      ),
    }));

  const handleDrawerClose = () => {
    setShowCustomTaskDrawer(false);
    setLoading(true);
    loadFarmTasks();
  };

  return !loading ? (
    <>
      <div className="padding-bottom-100 farm-documentation">
        <IconButton iconName="klim/plus" onClick={() => setShowCustomTaskDrawer(true)} type="secondary" className="margin-top-100" />
      </div>
      <Tabs items={getDocumentData()} />
      <CustomTaskDrawer visible={showCustomTaskDrawer} onClose={handleDrawerClose} farmId={farmId} farmCountry={farmCountry} />
      <SubmitTaskDrawer farmTask={selectedFarmTask} visible={showSubmitTaskDrawer} onClose={handleSubmitDocDrawerClose} onFinish={loadFarmTasks} isAdmin />
    </>
  ) : (
    <Spin />
  );
};
